import dayjsTZ, { dayjsInTz } from "../../../../utils/dayjs";
import { MIN_NOTE_HINT } from "./gracePeriod";

/** Soft early-arrival band relative to planned start (snap / on-time). */
export const PLANNED_EARLY_MINUTES = 15;
/** Soft late-stay band relative to planned end. */
export const PLANNED_LATE_MINUTES = 15;

function d(tz, value) {
  return tz ? dayjsInTz(tz, value) : dayjsTZ(value);
}

export function lateScheduleNoteText() {
  return `Vonesë ndaj orarit të planifikuar — shënoni arsyen e vonesës. (${MIN_NOTE_HINT})`;
}

export function earlyScheduleNoteText() {
  return `Fillim jashtë orarit (shumë herët) — shënoni arsyen. (${MIN_NOTE_HINT})`;
}

export function offScheduleClockInNoteText() {
  return `Fillim jashtë orarit të planifikuar — shënoni arsyen. (${MIN_NOTE_HINT})`;
}

export function earlyClockOutNoteText() {
  return `Mbarim jashtë orarit (para kohe) — shënoni arsyen. (${MIN_NOTE_HINT})`;
}

export function lateClockOutNoteText() {
  return `Mbarim jashtë orarit (pas mbarimit të planifikuar) — shënoni arsyen. (${MIN_NOTE_HINT})`;
}

/**
 * Soft schedule check for clock-in.
 * Never blocks for being early/late/off-schedule — only requires a note.
 *
 * @returns {{
 *   ok: boolean,
 *   noteRequired: boolean,
 *   kind: 'early'|'late'|'off_schedule'|null,
 *   reason: string|null,
 *   label: string|null
 * }}
 */
export function evaluatePlannedClockInWindow(
  plannedShift,
  clockInDate,
  options = {}
) {
  if (!plannedShift?.start) {
    return {
      ok: true,
      noteRequired: false,
      kind: null,
      reason: null,
      label: null,
    };
  }

  const label = plannedShift.label || null;
  const tz = options.timezone;
  const t = d(tz, clockInDate).valueOf();
  const start = d(tz, plannedShift.start).valueOf();
  const earliest = start - PLANNED_EARLY_MINUTES * 60 * 1000;
  const latest = plannedShift.end
    ? d(tz, plannedShift.end).valueOf()
    : start + 4 * 60 * 60 * 1000;
  const lateAfter = d(tz, plannedShift.start).add(30, "second").valueOf();

  if (t < earliest) {
    return {
      ok: true,
      noteRequired: true,
      kind: "early",
      reason: earlyScheduleNoteText(),
      label,
    };
  }
  if (t > latest) {
    return {
      ok: true,
      noteRequired: true,
      kind: "off_schedule",
      reason: offScheduleClockInNoteText(),
      label,
    };
  }
  // >1 minute after start (+30s buffer) = late → mandatory lateness note
  if (d(tz, clockInDate).diff(d(tz, lateAfter), "minutes", true) > 1) {
    return {
      ok: true,
      noteRequired: true,
      kind: "late",
      reason: lateScheduleNoteText(),
      label,
    };
  }

  return {
    ok: true,
    noteRequired: false,
    kind: null,
    reason: null,
    label,
  };
}

/**
 * Soft schedule check for clock-out.
 * Hard-fails only when out is before clock-in (invalid range).
 * Early leave / late stay → note required, never blocked for schedule.
 */
export function evaluatePlannedClockOutWindow(
  plannedShift,
  clockOutDate,
  clockInDate,
  options = {}
) {
  const label = plannedShift?.label || null;
  const tz = options.timezone;
  const t = d(tz, clockOutDate).valueOf();
  const earliest = clockInDate
    ? d(tz, clockInDate).valueOf()
    : plannedShift?.start
      ? d(tz, plannedShift.start).valueOf()
      : null;

  if (earliest != null && t < earliest) {
    return {
      ok: false,
      noteRequired: false,
      kind: "invalid",
      reason: "Ora e mbarimit nuk mund të jetë para fillimit të turnit.",
      label,
    };
  }

  if (!plannedShift?.end && !plannedShift?.start) {
    return {
      ok: true,
      noteRequired: false,
      kind: null,
      reason: null,
      label: null,
    };
  }

  if (plannedShift?.end) {
    const end = d(tz, plannedShift.end).valueOf();
    const earlyLeave = end - PLANNED_EARLY_MINUTES * 60 * 1000;
    const lateStay =
      end + PLANNED_LATE_MINUTES * 60 * 1000 + 30 * 1000;

    if (t < earlyLeave) {
      return {
        ok: true,
        noteRequired: true,
        kind: "early",
        reason: earlyClockOutNoteText(),
        label,
      };
    }
    if (t > lateStay) {
      return {
        ok: true,
        noteRequired: true,
        kind: "late",
        reason: lateClockOutNoteText(),
        label,
      };
    }
  }

  return {
    ok: true,
    noteRequired: false,
    kind: null,
    reason: null,
    label,
  };
}

export function plannedWindowHint(plannedShift, timezone) {
  if (!plannedShift?.start) return null;
  const tz = timezone;
  const start = d(tz, plannedShift.start).format("HH:mm");
  const end = plannedShift.end
    ? d(tz, plannedShift.end).format("HH:mm")
    : "—";
  return `Turni i planifikuar ${plannedShift.label || ""}: ${start}–${end}. Jashtë këtij orari (vonesë / herët / jashtë turnit) lejohet, por duhet shënim me arsye.`;
}

/**
 * Max time an employee may pick when editing = wall-clock "now".
 */
export function getEditTimeCeiling(now, _suggested, _options = {}) {
  const tz = _options?.timezone;
  return d(tz, now);
}

/**
 * @returns {boolean} true when candidate is later than ceiling (~0.8 min slack)
 */
export function isAboveEditCeiling(candidate, ceiling, timezone) {
  return d(timezone, candidate).diff(d(timezone, ceiling), "minutes", true) > 0.8;
}

/**
 * Roll-down snap for clock-out.
 */
export function snapRollDownToPlannedEnd(
  time,
  plannedEnd,
  lateMinutes = PLANNED_LATE_MINUTES,
  timezone
) {
  if (!plannedEnd) return null;
  const t = d(timezone, time).valueOf();
  const endMs = d(timezone, plannedEnd).valueOf();
  const upper = endMs + lateMinutes * 60 * 1000 + 30 * 1000;
  if (t > endMs && t < upper) {
    return d(timezone, plannedEnd).second(0).millisecond(0);
  }
  return null;
}
